import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ScaleLoader } from 'react-spinners'
import { Breadcrumbs } from '../../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import EditLyrics from '../../../Components/DashboardComponents/Lyrics/EditLyrics'
import ArtistAndSongName from '../../../Components/GeneralComponents/ArtistAndSongName/ArtistAndSongName'
import Loader from '../../../Components/GeneralComponents/Loader'
import PublicationBadge from '../../../Components/GeneralComponents/PublicationBadge'
import WaveSurfer from 'wavesurfer.js'
import checkmark_empty from '../../../Components/icons/checkmark_empty.png'
import checkmark_full from '../../../Components/icons/checkmark_full.png'
import classes from './index.module.css'
import FeelsSyncButton from '../../../Components/FeelsSync/FeelsSyncButton'
import DisabledOverlay from '../../../Components/GeneralComponents/DisabledOverlay/DisabledOverlay'
// import { Player, Controls } from '@lottiefiles/react-lottie-player'

import './EditSong.css'
import { useAPI } from '../../../Contexts/APIContext'
import {
  formatStringInNav,
  randomString,
} from '../../../Helpers/String+Extension'
import { useAuth } from '../../../Contexts/AuthContext'
import { isEmailInSongManagementTeam } from '../../../Helpers/RestrictedEmailsArray'
import PromptWithText from '../../../Components/DashboardComponents/Prompt/PromptWithText'
import errorToast from '../../../Components/Toasts/ErrorToast'
import successToast from '../../../Components/Toasts/SuccessToast'
import { StickyHeader } from '../../../Components/StickyHeader'
import { usePreventFullscreen } from '../../../Helpers/hooks/usePreventFullscreen'
import useDeviceAccessControl from '../../../Helpers/hooks/useDeviceAccessControl'
import DevicesRestrictionsNotification from '../../../Components/common/DevicesRestrictionsNotification'
import { Wrapper } from '../../../Components/page/Wrapper'
import { Header } from '../../../Components/page/Header'
import { Actions } from '../../../Components/page/Actions'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import useAdminAccess from '../../../services/hooks/useAdminAccess'
import useReadOnly from '../../../services/hooks/useReadOnly'
import { permissions } from '../../../constants/roles'
import { useCurrentItem } from '../../../Contexts/CurrentItemContext'
import useGetArtistData from '../../../Helpers/hooks/useGetArtistData'
import {
  detectEditType,
  validateLyricsForSave,
  hasLyricListChanges,
  splitLineAt,
  gapTimingForInsert,
  findIndexById,
  isVisibleLine,
  findPrevVisible,
  findNextVisible,
} from '../../../Helpers/lyricsEdit'

export default function EditSong() {
  const { getCurrentUser } = useAuth()
  const navigate = useNavigate()
  const {
    fetchLyricsToEdit,
    saveSongEdits,
    saveSongLyricsEdits,
    reportSong,
    cancelRequest,
    saveSongHideState,
  } = useAPI()

  const { id } = useParams()

  const hasDeviceAccess = useDeviceAccessControl()
  const { isInternal } = useAdminAccess()
  const { setItem } = useCurrentItem()

  const queryClient = useQueryClient()

  const hideStatusMutation = useMutation({
    mutationFn: () =>
      saveSongHideState(
        id,
        !songData?.is_public_visible,
        getCurrentUser()?.email
      ),
    onSuccess: () => {
      setSongData(prevData => ({
        ...prevData,
        is_public_visible: !prevData?.is_public_visible,
      }))
      queryClient.invalidateQueries({ queryKey: ['songData', id] })
    },
  })

  const handleOnHide = () => {
    hideStatusMutation.mutate()
  }

  const videoElement = useRef()
  const waveformElement = useRef()
  const videoColumnElement = useRef()
  const topOfLyricsColumnElement = useRef()
  usePreventFullscreen(videoElement)

  const [songData, setSongData] = useState(null)

  // Fetch artist data for navbar permission badge (artist always has organizations)
  const { data: artistData } = useGetArtistData({
    artistName: songData?.artist?.artist_name,
    enabled: !!songData?.artist?.artist_name,
  })

  const { isReadOnly: isSongReadOnly } = useReadOnly(permissions.r.music_song, artistData)

  // Redirect read-only users to the song details page
  useEffect(() => {
    if (artistData && isSongReadOnly) {
      navigate(`/song/${id}`, { replace: true })
    }
  }, [artistData, isSongReadOnly, navigate, id])

  const [isSyncVerified, setIsSyncVerified] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [controller, setController] = useState(null)
  const [waveform, setWaveform] = useState(null)

  const [hasEdited, setHasEdited] = useState(false)
  const [isSavingSong, setIsSavingSong] = useState(false)
  const [isLoopingLines, setIsLoopingLines] = useState(true)

  const [isShiftDown, setIsShiftDown] = useState(false)
  const [isAltDown, setIsAltDown] = useState(false)
  const [isXDown, setIsXDown] = useState(false)

  const [currentlyHighlightedLine, setCurrentlyHighlightedLine] = useState(null)

  const [currentlySelectedLine, setCurrentlySelectedLine] = useState(null)
  const [videoOffset, setVideoOffset] = useState(0)
  const [feelsSyncStatus, setFeelsSyncStatus] = useState('idle')
  const [originalLyrics, setOriginalLyrics] = useState([])
  const [lyrics, setLyrics] = useState([])

  let didAddEventListeners = false
  const currentVideoOffset = Number(videoOffset) || 0

  // Lock editing when sync-verified (read-only) or mid-save: edits made during
  // the save roundtrip would be overwritten by the success-handler reconcile.
  const editingLocked = isSyncVerified || isSavingSong

  const [errorMessage, setErrorMessage] = useState(null)
  const [isFeedbackPrompt, setIsFeedbackPrompt] = useState(false)
  const [isTransferEndTimingsConfirm, setIsTransferEndTimingsConfirm] = useState(false)

  const [showLyricsEditText, setShowLyricsEditText] = useState(false)
  const [editingLineId, setEditingLineId] = useState(null)

  // Set current item for navbar permission badge (use artist data which always has organizations)
  useEffect(() => {
    if (artistData) {
      setItem(artistData)
    }
  }, [artistData, setItem])

  const [timer, setTimer] = useState(null)
  const [videoDuration, setVideoDuration] = useState(0)

  // console.log({ videoDuration })

  // Safe video play function to prevent interruption errors
  const safeVideoPlay = async () => {
    try {
      if (videoElement.current && videoElement.current.readyState >= 2) {
        await videoElement.current.play()
      }
    } catch (error) {
      // Silently ignore play interruption errors - they're harmless
      if (!error.message.includes('interrupted') && !error.name.includes('AbortError')) {
        console.warn('Video play error:', error)
      }
    }
  }

  useEffect(() => {
    fetchData()
    return () => {
      clearInterval(timer)
      setTimer(null)
    }
  }, [])

  //Hot keys setup
  useEffect(() => {
    if (!didAddEventListeners && !isFeedbackPrompt) {
      document.onkeydown = e => {
        if (!showLyricsEditText && !editingLineId) {
          e = e || window.event
          if (e.shiftKey) {
            setIsShiftDown(true)
          }
          if (e.key.toLowerCase() === 'z') {
            setIsAltDown(true)
          }
          if (e.key.toLowerCase() === 'x') {
            setIsXDown(true)
          }
          if (!showLyricsEditText && e.code === 'Space') {
            e.preventDefault()
          }
        }
      }

      document.onkeyup = e => {
        if (!showLyricsEditText && !isFeedbackPrompt && !editingLineId) {
          e = e || window.event
          const indexOfSelectedLine = lyrics.indexOf(currentlySelectedLine)
          if (e.key === 'ArrowDown') {
            if (indexOfSelectedLine === -1 && lyrics.length > 0) {
              handleLyricSelected(lyrics[0])
            } else if (lyrics.length - 1 > indexOfSelectedLine) {
              for (var i = indexOfSelectedLine + 1; i < lyrics.length; i++) {
                if (lyrics[i].line !== '') {
                  handleLyricSelected(lyrics[i])
                  break
                }
              }
            }
          } else if (e.key === 'ArrowUp') {
            if (indexOfSelectedLine >= 1) {
              for (var j = indexOfSelectedLine - 1; j >= 0; j--) {
                if (lyrics[j].line !== '') {
                  handleLyricSelected(lyrics[j])
                  break
                }
              }
            } else if (indexOfSelectedLine === 0) {
              handleLyricSelected(lyrics[indexOfSelectedLine])
            }
          } else if (e.key.toLowerCase() === 's' && hasEdited) {
            saveCurrentEdits()
          } else if (e.key.toLowerCase() === 'm') {
            videoElement.current.muted = !videoElement.current.muted
          } else if (e.key.toLowerCase() === 'p') {
            if (videoElement.current.paused) {
              safeVideoPlay()
            } else {
              videoElement.current.pause()
            }
            videoElement.current.muted = !videoElement.current.muted
          } else if (e.key.toLowerCase() === 'y') {
            setIsLoopingLines(!isLoopingLines)
          } else if (e.key.toLowerCase() === 'u') {
            handleSetCurrentVideoPlayerToLineAction()
            if (indexOfSelectedLine !== -1) {
              for (var ui = indexOfSelectedLine + 1; ui < lyrics.length; ui++) {
                if (lyrics[ui].line !== '') {
                  handleLyricSelected(lyrics[ui])
                  break
                }
              }
            }
          } else if (
            (e.key.toLowerCase() === 'q' ||
              e.key.toLowerCase() === 'w' ||
              e.key.toLowerCase() === 'e' ||
              e.key.toLowerCase() === 'r') &&
            indexOfSelectedLine !== -1
          ) {
            handleIncrementDecrementButton(
              lyrics[indexOfSelectedLine],
              e.key.toLowerCase() === 'q' || e.key.toLowerCase() === 'w',
              e.key.toLowerCase() === 'q' || e.key.toLowerCase() === 'e',
              isShiftDown ? 1000 : isAltDown ? 20 : isXDown ? 300 : 100,
              true
            )
          } else if (
            (e.key.toLowerCase() === 'h' ||
              e.key.toLowerCase() === 'j' ||
              e.key.toLowerCase() === 'k' ||
              e.key.toLowerCase() === 'l') &&
            indexOfSelectedLine !== -1
          ) {
            handleIncrementDecrementToLineAndFollowingLines(
              lyrics[indexOfSelectedLine],
              e.key.toLowerCase() === 'h' || e.key.toLowerCase() === 'j',
              e.key.toLowerCase() === 'h' || e.key.toLowerCase() === 'k',
              isShiftDown ? 1000 : isAltDown ? 20 : isXDown ? 300 : 100,
              true
            )
          } else if (e.code === 'Space') {
            if (videoElement.current.paused) {
              safeVideoPlay()
            } else {
              videoElement.current.pause()
            }
            videoElement.current.muted = !videoElement.current.muted
          } else if (
            e.key === 'Enter' &&
            !editingLocked &&
            currentlySelectedLine !== null &&
            !currentlySelectedLine.isDeleted &&
            !['BUTTON', 'INPUT', 'TEXTAREA', 'A'].includes(e.target?.tagName)
          ) {
            setEditingLineId(currentlySelectedLine.identifier)
          }
          if (e.key === 'Shift') {
            setIsShiftDown(false)
          }
          if (e.key.toLowerCase() === 'z') {
            setIsAltDown(false)
          }
          if (e.key.toLowerCase() === 'x') {
            setIsXDown(false)
          }
        }
      }
      didAddEventListeners = true
    }
    return () => {
      document.onkeydown = null
      document.onkeyup = null
    }
  }, [
    lyrics,
    originalLyrics,
    songData,
    currentlySelectedLine,
    hasEdited,
    isLoopingLines,
    isShiftDown,
    isAltDown,
    isXDown,
    showLyricsEditText,
    isFeedbackPrompt,
    editingLineId,
    isSyncVerified,
    isSavingSong,
  ])

  useEffect(() => {
    if (currentlySelectedLine !== null) {
      setVideoPlayerAtLine(currentlySelectedLine)
    }
  }, [videoOffset])

  useEffect(() => {
    setupWaveform()
  }, [songData])

  useEffect(() => {
    setupWaveform()
  }, [waveformElement])

  useEffect(() => {
    clearInterval(timer)
    const newTimer = setInterval(() => {
      handleOnTimeUpdate()
    }, 5)
    setTimer(newTimer)

    return () => {
      clearInterval(timer)
      setTimer(null)
    }
  }, [isLoopingLines, lyrics, currentlySelectedLine])

  function fetchData() {
    setErrorMessage(null)
    cancelRequest(controller)
    setIsLoading(true)

    const newController = fetchLyricsToEdit(
      id,
      data => {
        setIsLoading(false)
        setController(null)
        data
          .json()
          .then(json => {
            setSongData(json)
            let lyrics = attachIdentifiersAndFlags(json.lyrics_sync, json.timing_flags)
            setLyrics(lyrics)
            setOriginalLyrics(lyrics)
            if (
              json.is_sync_verified !== undefined ||
              json.is_sync_verified !== null
            ) {
              setIsSyncVerified(json.is_sync_verified)
            }
            if (json.lyrics_adj) {
              setVideoOffset(json.lyrics_adj)
            }
          })
          .catch(e => {
            setErrorMessage(e.message)
          })
      },
      error => {
        setController(null)
        setIsLoading(false)
        setErrorMessage(error)
      }
    )
    setController(newController)
  }

  function saveEdits(onComplete) {
    setVideoOffset(currentVideoOffset)
    setErrorMessage(null)
    setIsSavingSong(true)
    cancelRequest(controller)

    // Validate timing before sending to server
    try {
      prepareLyricsToSave(lyrics)
    } catch (error) {
      errorToast(error.message)
      setIsSavingSong(false)
      if (onComplete) onComplete(error)
      return
    }

    const newController = saveSongEdits(
      id,
      currentVideoOffset,
      lyrics,
      isSyncVerified,
      songData?.feels_locked ?? false,
      getCurrentUser()?.email,
      data => {
        setIsSavingSong(false)
        setController(null)
        data
          .json()
          .then(() => {
            setHasEdited(false)
            // Fire-and-confirm: don't repaint from the response. Reset the dirty
            // baseline from local lyrics so timing-only saves keep routing to /sync.
            setOriginalLyrics(
              lyrics.filter(l => !l.isDeleted).map(l => ({ ...l }))
            )
            successToast('Timing saved.')
            if (onComplete) onComplete(null)
          })
          .catch(e => {
            errorToast(e.message)
            if (onComplete) onComplete(e)
          })
          .finally(() => {
            queryClient.invalidateQueries({
              queryKey: ['songData', id],
            })
          })
      },
      error => {
        // Handle structured error objects from validation failures
        if (error && typeof error === 'object' && error.message) {
          errorToast(error.message)
        } else {
          errorToast(error)
        }
        setIsSavingSong(false)
        setController(null)
        if (onComplete) onComplete(error)
      }
    )
    setController(newController)
  }

  function saveLyricsEdits(newLyrics, forceEditType = null, onComplete) {
    setErrorMessage(null)
    setIsSavingSong(true)
    cancelRequest(controller)

    const wasInTextareaMode = showLyricsEditText

    let lyricsToSave
    try {
      lyricsToSave = prepareLyricsToSave(newLyrics)
    } catch (error) {
      errorToast(error.message)
      setIsSavingSong(false)
      if (onComplete) onComplete(error)
      return
    }

    const editType = forceEditType || detectEditType(originalLyrics, newLyrics)

    const newController = saveSongLyricsEdits(
      id,
      lyricsToSave,
      getCurrentUser()?.email,
      data => {
        setIsSavingSong(false)
        setController(null)
        data
          .json()
          .then(() => {
            setHasEdited(false)

            // Reconcile local state to the baseline /lyrics/edit just published
            // (baked timings, backend's Math.trunc, same identifiers, drop
            // deleted, offset baked) so the next /sync isn't rejected as stale.
            const liveLines = newLyrics.filter(l => !l.isDeleted)
            const reconciled = liveLines.map((live, i) => {
              const saved = lyricsToSave[i]
              // Blank spacer lines carry no timing — keep them as-is.
              if (!saved || live.line === '' || saved.line === '') {
                return { ...live, isNew: false }
              }
              const tStart = Math.trunc(saved.t_start)
              const tEnd = Math.trunc(saved.t_end)
              return {
                ...live,
                isNew: false,
                line: saved.line,
                t_start: tStart,
                t_end: tEnd,
                duration: tEnd - tStart,
                milliseconds: tStart,
                lrc_timestamp: tToTimestamp(tStart),
                // adjustments are now folded into t_start/t_end above
                start_adj: 0,
                end_adj: 0,
              }
            })
            setLyrics(reconciled)
            setOriginalLyrics(reconciled.map(l => ({ ...l })))
            // Offset is baked into the reconciled timings now → matched pair.
            setVideoOffset(0)

            // Re-point selection/highlight at the reconciled objects (same
            // identifier) so no stale object reference survives the swap.
            const remap = sel =>
              sel
                ? reconciled.find(l => l.identifier === sel.identifier) ?? null
                : null
            setCurrentlySelectedLine(prev => remap(prev))
            setCurrentlyHighlightedLine(prev => remap(prev))

            // Invalidate song data queries to refresh other pages
            queryClient.invalidateQueries({ queryKey: ['songData', id] })

            // Clear any stored FeelsSync status
            if (songData?.f_id) {
              localStorage.removeItem(`feelssync_${songData.f_id}`)
            }
            // /lyrics/edit can't write sync-verification (only /sync does), so
            // follow up with a /sync (same published set, offset 0) to persist
            // the current Sync Verified state on a lyrics-path save.
            saveSongEdits(
              id,
              0,
              lyricsToSave,
              isSyncVerified,
              songData?.feels_locked ?? false,
              getCurrentUser()?.email,
              () => {},
              () => {
                errorToast(
                  'Lyrics saved, but updating Sync Verified failed — re-save to retry.'
                )
              }
            )
            if (wasInTextareaMode) {
              handleChangeOfEditMode(false)
            }

            // Notify that lyrics have been updated - this should trigger feel updates
            // Note: This would require backend support to update all related feels
            successToast('Lyrics updated. Existing Feels may need manual refresh.')
            if (onComplete) onComplete(null)
          })
          .catch(e => {
            errorToast(e.message)
            if (onComplete) onComplete(e)
          })
      },
      error => {
        // Handle structured error objects from validation failures
        if (error && typeof error === 'object' && error.message) {
          errorToast(error.message)
        } else {
          errorToast(error)
        }
        setIsSavingSong(false)
        setController(null)
        if (onComplete) onComplete(error)
      },
      editType
    )
    setController(newController)
  }

  function saveCurrentEdits(onComplete) {
    if (showLyricsEditText) {
      handleOnNewLyricsSave(onComplete)
    } else if (hasLyricListChanges(originalLyrics, lyrics)) {
      saveLyricsEdits(lyrics, null, onComplete)
    } else {
      saveEdits(onComplete)
    }
  }

  // Auto-save-before-FeelsSync: returns a Promise that resolves once any
  // pending lyric edits have been persisted. If the user has no unsaved
  // edits, resolves immediately. Used by FeelsSyncButton to ensure the
  // backend has the latest lyrics before we ask it to retime them.
  function beforeFeelsSync() {
    return new Promise((resolve, reject) => {
      if (!hasEdited) {
        resolve()
        return
      }
      saveCurrentEdits((error) => {
        if (error) reject(error)
        else resolve()
      })
    })
  }

  // feelssync-v2 review flags index into the lyric array by position;
  // attach them to the items so they follow the line through edits.
  function attachIdentifiersAndFlags(lyricsSync, timingFlags) {
    const flagsByIndex = {}
    for (const f of timingFlags || []) {
      flagsByIndex[f.line_index] = f
    }
    return (lyricsSync || []).map((item, index) => ({
      ...item,
      identifier: randomString(10),
      ...(flagsByIndex[index] ? { sync_flags: flagsByIndex[index] } : {})
    }))
  }

  // Completion handler for the FeelsSync button (both its queued FeelsSync
  // and right-click QuickTime paths): swap in the freshly timed lyrics and
  // reset editing state.
  function handleFeelsSyncComplete(data) {
    // Pause video to prevent play() interruption errors
    if (videoElement.current) {
      videoElement.current.pause()
    }

    setSongData(prevData => ({
      ...prevData,
      ...data,
      lyrics_sync: attachIdentifiersAndFlags(data.lyrics_sync, data.timing_flags)
    }))
    setLyrics(attachIdentifiersAndFlags(data.lyrics_sync, data.timing_flags))
    setOriginalLyrics(attachIdentifiersAndFlags(data.lyrics_sync, data.timing_flags))

    // Reset any selected line to avoid timing issues
    setCurrentlySelectedLine(null)
    setCurrentlyHighlightedLine(null)

    // Clear video offset since new sync data should have proper timing
    setVideoOffset(0)

    // Unlock the editor after applying the new sync data: reset FeelsSync
    // status to 'idle' so the DisabledOverlay comes down without a refresh.
    setFeelsSyncStatus('idle')
  }

  function handleTransferEndTimings() {
    const newLyrics = lyrics.map(l => ({ ...l }))
    for (let i = 0; i < newLyrics.length; i++) {
      if (!('t_start' in newLyrics[i])) continue
      // Find next non-blank lyric
      let nextLyric = null
      for (let j = i + 1; j < newLyrics.length; j++) {
        if ('t_start' in newLyrics[j]) {
          nextLyric = newLyrics[j]
          break
        }
      }
      if (nextLyric) {
        const endTime = nextLyric.t_start + (nextLyric.start_adj || 0)
        newLyrics[i].end_adj = endTime - newLyrics[i].t_end
      } else {
        // Last non-blank line — only fix if end <= start (broken timing)
        const effectiveStart = newLyrics[i].t_start + (newLyrics[i].start_adj || 0)
        const effectiveEnd = newLyrics[i].t_end + (newLyrics[i].end_adj || 0)
        if (effectiveEnd <= effectiveStart) {
          const desiredEnd = Math.min(effectiveStart + 15000, videoDuration - currentVideoOffset)
          newLyrics[i].end_adj = desiredEnd - newLyrics[i].t_end
        }
      }
    }
    setLyrics(newLyrics)
    if (!hasLyricListChanges(originalLyrics, lyrics)) {
      setOriginalLyrics(newLyrics)
    }
    setHasEdited(true)
    setIsTransferEndTimingsConfirm(false)
  }

  function prepareLyricsToSave(newLyrics) {
    const liveLyrics = newLyrics.filter(l => !l.isDeleted)

    const validationError = validateLyricsForSave(liveLyrics, currentVideoOffset)
    if (validationError) {
      throw new Error(validationError)
    }

    var lyricsToSave = []
    for (var i = 0; i < liveLyrics.length; i++) {
      if (liveLyrics[i].line === '') {
        lyricsToSave.push({ line: '' })
      } else {
        const startTime =
          currentVideoOffset +
          liveLyrics[i].t_start +
          (liveLyrics[i].start_adj !== undefined ? liveLyrics[i].start_adj : 0)
        const endTime =
          currentVideoOffset +
          liveLyrics[i].t_end +
          (liveLyrics[i].end_adj !== undefined ? liveLyrics[i].end_adj : 0)
        lyricsToSave.push({
          line: liveLyrics[i].line,
          t_end: endTime,
          t_start: startTime,
          duration: endTime - startTime,
          milliseconds: startTime,
          lrc_timestamp: tToTimestamp(startTime),
        })
      }
    }
    return lyricsToSave
  }

  function insertLineAt(anchorIdentifier, position) {
    // position: 'above' or 'below' the anchor line
    const idx = findIndexById(lyrics, anchorIdentifier)
    if (idx === -1) return
    const insertAt = position === 'above' ? idx : idx + 1
    const prevLine = findPrevVisible(lyrics, insertAt)
    const nextLine = findNextVisible(lyrics, insertAt)
    const { t_start, t_end } = gapTimingForInsert(prevLine, nextLine, videoDuration)
    const newLine = {
      identifier: randomString(10),
      isNew: true,
      line: '',
      t_start,
      t_end,
      duration: t_end - t_start,
      milliseconds: t_start,
      lrc_timestamp: tToTimestamp(t_start),
      start_adj: 0,
      end_adj: 0,
    }
    const next = [...lyrics.slice(0, insertAt), newLine, ...lyrics.slice(insertAt)]
    setLyrics(next)
    setHasEdited(true)
    setEditingLineId(newLine.identifier)
  }

  function deleteLineAt(identifier) {
    const next = lyrics.map(l =>
      l.identifier === identifier ? { ...l, isDeleted: true } : l
    )
    setLyrics(next)
    setHasEdited(true)
    if (editingLineId === identifier) setEditingLineId(null)
    if (currentlySelectedLine?.identifier === identifier) {
      setCurrentlySelectedLine(null)
    }
  }

  function undoDeleteLineAt(identifier) {
    const next = lyrics.map(l =>
      l.identifier === identifier ? { ...l, isDeleted: false } : l
    )
    setLyrics(next)
  }

  function splitLineAtCursor(identifier, cursorIndex) {
    // Enter-in-textarea = "I'm done with this line, move on" — release any loop
    setCurrentlySelectedLine(null)
    const idx = findIndexById(lyrics, identifier)
    if (idx === -1) return
    if (lyrics[idx].isDeleted) return
    const line = lyrics[idx]
    if (cursorIndex === 0) {
      insertLineAt(identifier, 'above')
      return
    }
    if (cursorIndex >= (line.line || '').length) {
      insertLineAt(identifier, 'below')
      return
    }
    const { first, second } = splitLineAt(line, cursorIndex, () => randomString(10))
    first.milliseconds = first.t_start
    first.duration = first.t_end - first.t_start
    first.lrc_timestamp = tToTimestamp(first.t_start)
    second.milliseconds = second.t_start
    second.duration = second.t_end - second.t_start
    second.lrc_timestamp = tToTimestamp(second.t_start)
    const next = [
      ...lyrics.slice(0, idx),
      first,
      second,
      ...lyrics.slice(idx + 1),
    ]
    setLyrics(next)
    setHasEdited(true)
    setEditingLineId(second.identifier)
  }

  function setLineText(identifier, newText) {
    const next = lyrics.map(l =>
      l.identifier === identifier ? { ...l, line: newText } : l
    )
    setLyrics(next)
    setHasEdited(true)
  }

  function startEditing(identifier) {
    setEditingLineId(identifier)
  }

  function stopEditing() {
    setEditingLineId(null)
  }

  function stopEditingAndPurgeIfEmpty(committedId) {
    // The caller passes the identifier of the textarea that just committed.
    // This avoids closure staleness — see the Tab-advance race: by the time
    // an unmounting textarea's blur fires, editingLineId may already point at
    // the next line, so reading from closure would clear the *new* editing id.
    if (committedId != null) {
      setLyrics(prev => {
        const idx = prev.findIndex(l => l.identifier === committedId)
        if (idx !== -1) {
          const line = prev[idx]
          if (line.isNew && (!line.line || line.line === '')) {
            return prev.filter(l => l.identifier !== committedId)
          }
        }
        return prev
      })
    }
    // Only null out if we're still editing the same line the caller meant.
    setEditingLineId(prev => (prev === committedId ? null : prev))
  }

  function advanceEditingToNext() {
    const idx = findIndexById(lyrics, editingLineId)
    if (idx === -1) {
      setEditingLineId(null)
      return
    }
    for (let i = idx + 1; i < lyrics.length; i++) {
      if (isVisibleLine(lyrics[i])) {
        setEditingLineId(lyrics[i].identifier)
        return
      }
    }
    setEditingLineId(null)
  }

  function zeroPad(num, places) {
    return String(num).padStart(places, '0')
  }

  function tToTimestamp(t) {
    if (!t) {
      return '[00:00.00]'
    }

    let s_raw = Math.floor(t / 1000.0)

    let m_ts = Math.floor(s_raw / 60)
    let s_ts = s_raw % 60
    let ss_ts = Math.round(t - s_raw * 1000) / 10.0

    return `[${zeroPad(m_ts, 2)}:${zeroPad(s_ts, 2)}.${zeroPad(ss_ts, 2)}]`
  }

  function setupWaveform() {
    if (
      waveformElement.current !== undefined &&
      videoElement !== undefined &&
      songData !== undefined &&
      waveform === null
    ) {
      const newWaveform = WaveSurfer.create({
        barWidth: 3,
        barRadius: 3,
        barGap: 2,
        barMinHeight: 1,
        cursorWidth: 2,
        container: waveformElement.current,
        backend: 'MediaElement',
        height: 80,
        responsive: true,
        interact: true,
        waveColor: '#C4C4C4',
        progressColor: '#FF00FF',
        cursorColor: 'white',
        hideScrollbar: true,
      })
      var mediaElt = videoElement.current
      newWaveform.load(mediaElt)
      videoElement.current.controls = 'controls'
      videoElement.current.muted = true
      
      // Delay play to avoid conflicts with other video operations
      setTimeout(safeVideoPlay, 100)
      setWaveform(newWaveform)
    }
  }

  function handleLyricSelected(line) {
    if (
      currentlySelectedLine !== null &&
      currentlySelectedLine.identifier === line.identifier
    ) {
      setCurrentlySelectedLine(null)
    } else {
      setCurrentlySelectedLine(line)
      setVideoPlayerAtLine(line)
    }
  }

  function setVideoPlayerAtLine(line) {
    const currentStartTime =
      currentVideoOffset +
      line.t_start +
      (line.start_adj !== undefined ? line.start_adj : 0)
    videoElement.current.currentTime = currentStartTime / 1000
  }

  function setVideoPlayerAtEndOfALine(line) {
    const currentEndTime =
      currentVideoOffset +
      line.t_end +
      (line.end_adj !== undefined ? line.end_adj : 0) -
      1000
    videoElement.current.currentTime = currentEndTime / 1000
  }

  function handleOnTimeUpdate() {
    if (videoElement.current === undefined || videoElement.current === null) {
      return
    }
    const line = lyrics.find(item => {
      const currentStartTime =
        currentVideoOffset +
        item.t_start +
        (item.start_adj !== undefined ? item.start_adj : 0)
      const currentEndTime =
        currentVideoOffset +
        item.t_end +
        (item.end_adj !== undefined ? item.end_adj : 0)
      return (
        videoElement.current.currentTime * 1000 > currentStartTime &&
        videoElement.current.currentTime * 1000 < currentEndTime
      )
    })
    if (line !== undefined) {
      setCurrentlyHighlightedLine(line)
    } else {
      setCurrentlyHighlightedLine(null)
    }

    if (currentlySelectedLine !== null && isLoopingLines) {
      const currentStartTime = getCurrentSelectedLineStartTime()
      const currentEndTime = getCurrentSelectedLineEndTime()
      // This is very handy for debugging of the line looping
      // console.log("START: " + currentStartTime)
      // console.log("END: " + currentEndTime)
      // console.log("CURRENT: " + Math.round((videoElement.current.currentTime + Number.EPSILON) * 1000) / 1000)
      if (
        Math.round((videoElement.current.currentTime + Number.EPSILON) * 1000) /
          1000 <
          currentStartTime / 1000 ||
        Math.round((videoElement.current.currentTime + Number.EPSILON) * 1000) /
          1000 +
          0.05 >
          currentEndTime / 1000
      ) {
        setVideoPlayerAtLine(currentlySelectedLine)
        // videoElement.current.play()
      }
    } else if (
      videoElement.current.currentTime >= videoElement.current.duration
    ) {
      videoElement.current.currentTime = 0
      safeVideoPlay()
    }

    // requestAnimationFrame(handleOnTimeUpdate)
  }

  function getCurrentSelectedLineStartTime() {
    if (currentlySelectedLine === null || currentlySelectedLine === undefined) {
      return null
    }
    return (
      currentVideoOffset +
      currentlySelectedLine.t_start +
      (currentlySelectedLine.start_adj !== undefined
        ? currentlySelectedLine.start_adj
        : 0)
    )
  }

  function getCurrentSelectedLineEndTime() {
    if (currentlySelectedLine === null || currentlySelectedLine === undefined) {
      return null
    }
    return (
      currentVideoOffset +
      currentlySelectedLine.t_end +
      (currentlySelectedLine.end_adj !== undefined
        ? currentlySelectedLine.end_adj
        : 0)
    )
  }

  function handleSetCurrentVideoPlayerToLineAction() {
    if (editingLocked) return
    const newVideoOffset = Math.floor(videoElement.current.currentTime * 1000)

    if (currentlySelectedLine === null) {
      setVideoOffset(newVideoOffset)
      setHasEdited(true)
      setCurrentlyHighlightedLine(null)
    } else {
      const indexOfSelectedLine = lyrics.indexOf(currentlySelectedLine)
      const newLineOffset =
        newVideoOffset -
        lyrics[indexOfSelectedLine].t_start -
        currentVideoOffset -
        (lyrics[indexOfSelectedLine].start_adj !== undefined
          ? lyrics[indexOfSelectedLine].start_adj
          : 0)
      var newLyrics = [...lyrics]
      newLyrics.splice(0, indexOfSelectedLine)
      for (var i = 0; i < newLyrics.length; i++) {
        let line = newLyrics[i]
        handleIncrementDecrementButton(line, true, false, newLineOffset, false)
        handleIncrementDecrementButton(line, false, false, newLineOffset, false)
      }
      setVideoPlayerAtLine(newLyrics[0])
    }
  }

  function handleIncrementDecrementToLineAndFollowingLines(
    line,
    isStart,
    isDecrement,
    value,
    setPlayer
  ) {
    if (editingLocked) return
    if (currentlySelectedLine !== null) {
      const indexOfSelectedLine = lyrics.indexOf(currentlySelectedLine)
      var newLyrics = [...lyrics]
      newLyrics.splice(0, indexOfSelectedLine)
      for (var i = 0; i < newLyrics.length; i++) {
        let line = newLyrics[i]
        if (line.line !== '') {
          handleIncrementDecrementButton(
            line,
            isStart,
            isDecrement,
            value,
            setPlayer
          )
        }
      }
      setVideoPlayerAtLine(newLyrics[0])
    }
  }

  function handleOnVideoOffsetChange(e) {
    if (editingLocked) return
    let inputValue = e.target.value
    const testInputValue = /[^\d-]/.test(inputValue)
    if (
      videoOffset.toString() === '0' &&
      videoOffset.toString().length === 1 &&
      (inputValue === '-' || !testInputValue)
    ) {
      inputValue = inputValue.substring(1)
    }

    inputValue = inputValue.replace(/[^0-9-]/g, '')

    if (inputValue.lastIndexOf('-') > 0) {
      const index = inputValue.lastIndexOf('-')
      inputValue =
        inputValue.substring(0, index) + inputValue.substring(index + 1)
    }

    if (inputValue.length > 1) {
      inputValue = inputValue.replace(/^(-)?0+(?=\d)/, '$1')
    }

    setVideoOffset(inputValue)
    setHasEdited(true)
    setCurrentlyHighlightedLine(null)
  }

  function handleIncrementDecrementButton(
    line,
    isStart,
    isDecrement,
    value,
    setPlayer
  ) {
    if (editingLocked) return
    const indexOfSelectedLine = lyrics.indexOf(line)
    const newLyrics = [...lyrics]
    if (
      newLyrics[indexOfSelectedLine].start_adj === undefined ||
      newLyrics[indexOfSelectedLine].start_adj === null
    ) {
      newLyrics[indexOfSelectedLine].start_adj = 0
    }
    if (
      newLyrics[indexOfSelectedLine].end_adj === undefined ||
      newLyrics[indexOfSelectedLine].end_adj === null
    ) {
      newLyrics[indexOfSelectedLine].end_adj = 0
    }

    if (isStart) {
      if (isDecrement) {
        newLyrics[indexOfSelectedLine].start_adj -= value //isShiftDown ? 1000 : 100
      } else {
        newLyrics[indexOfSelectedLine].start_adj += value //isShiftDown ? 1000 : 100
      }
      if (setPlayer) {
        setVideoPlayerAtLine(newLyrics[indexOfSelectedLine])
      }
    } else {
      if (isDecrement) {
        newLyrics[indexOfSelectedLine].end_adj -= value //isShiftDown ? 1000 : 100
      } else {
        const newValue = newLyrics[indexOfSelectedLine].end_adj + value
        newValue + newLyrics[indexOfSelectedLine].t_end + Number(videoOffset) >
        videoDuration
          ? (newLyrics[indexOfSelectedLine].end_adj =
              videoDuration -
              newLyrics[indexOfSelectedLine].t_end -
              videoOffset)
          : (newLyrics[indexOfSelectedLine].end_adj = newValue)
      }
      if (setPlayer) {
        setVideoPlayerAtEndOfALine(newLyrics[indexOfSelectedLine])
      }
    }
    setLyrics(newLyrics)
    if (!hasLyricListChanges(originalLyrics, lyrics)) {
      setOriginalLyrics(newLyrics)
    }
    setHasEdited(true)
  }

  function handleStartEndInputValueChange(line, value, isStartTime) {
    const indexOfSelectedLine = lyrics.indexOf(line)
    const newLyrics = [...lyrics]

    if (
      newLyrics[indexOfSelectedLine].start_adj === undefined ||
      newLyrics[indexOfSelectedLine].start_adj === null
    ) {
      newLyrics[indexOfSelectedLine].start_adj = 0
    }
    if (
      newLyrics[indexOfSelectedLine].end_adj === undefined ||
      newLyrics[indexOfSelectedLine].end_adj === null
    ) {
      newLyrics[indexOfSelectedLine].end_adj = 0
    }

    if (isStartTime) {
      const adj =
        value -
        newLyrics[indexOfSelectedLine].t_start -
        newLyrics[indexOfSelectedLine].start_adj -
        currentVideoOffset
      newLyrics[indexOfSelectedLine].start_adj += adj
    } else {
      const duration = value > videoDuration ? videoDuration : value
      const adj =
        duration -
        newLyrics[indexOfSelectedLine].t_end -
        newLyrics[indexOfSelectedLine].end_adj -
        currentVideoOffset
      newLyrics[indexOfSelectedLine].end_adj += adj
    }
    setLyrics(newLyrics)
    if (!hasLyricListChanges(originalLyrics, lyrics)) {
      setOriginalLyrics(newLyrics)
    }
    setHasEdited(true)
  }

  function handleOnNavigationToAllSongs() {
    cancelRequest(controller)
    navigate('/songs/all')
  }

  function handleOnNavigationToSpecificArtistClick() {
    cancelRequest(controller)
    navigate('/artist/' + songData.artist.artist_name)
  }

  function handleOnNavigationToArtistSongsClick() {
    cancelRequest(controller)
    navigate('/songs/artist/' + songData.artist.artist_name)
  }

  function handleOnNavigationToSongClick() {
    navigate('/song/' + songData.f_id)
  }

  function handleOnShowReport() {
    setIsFeedbackPrompt(true)
  }

  function handleOnReport(comment) {
    setIsFeedbackPrompt(false)
    errorToast('Submitting...')

    reportSong(
      id,
      comment,
      getCurrentUser()?.email,
      () => {
        successToast('Successfully reported this Song - thank you!')
      },
      error => {
        errorToast(error)
      }
    )
  }

  function handleChangeOfEditMode(isCancel) {
    if (isCancel) {
      setLyrics(originalLyrics)
    }
    const textInputField = document.getElementsByClassName(
      'lyrics-freeform-textarea'
    )[0]
    textInputField.value = getLyricsInOneTextBlock()
    setShowLyricsEditText(!showLyricsEditText)
    videoElement.current.pause()
  }

  function getLyricsInOneTextBlock() {
    var lyricsText = ''
    for (var i = 0; i < lyrics.length; i++) {
      lyricsText += lyrics[i].line
      if (i < lyrics.length - 1) {
        lyricsText += '\n'
      }
    }
    return lyricsText
  }

  function lyricsTextIsChanging() {
    setHasEdited(true)
    const textInputField = document.getElementsByClassName(
      'lyrics-freeform-textarea'
    )[0]
    if (textInputField !== undefined && textInputField !== null) {
      processNewLyricsText(textInputField.value)
    }
  }

  function handleOnNewLyricsSave(onComplete) {
    const textValue = document.getElementsByClassName(
      'lyrics-freeform-textarea'
    )[0].value
    const newLyrics = processNewLyricsText(textValue)
    saveLyricsEdits(newLyrics, 'compose', onComplete)
  }

  function processNewLyricsText(textValue) {
    const newLyricsRaw = textValue.split('\n')
    const copyOfCurrentLyricsForTimings = [...lyrics].filter(item => {
      return item.line !== ''
    })
    let newLyrics = []
    for (let i = 0; i < newLyricsRaw.length; i++) {
      if (newLyricsRaw[i] == '') {
        newLyrics.push({
          line: '',
          identifier: randomString(10),
        })
        continue
      } else {
        var timing = {
          duration: 0,
          end_adj: 0,
          milliseconds: 0,
          start_adj: 0,
          t_end: 0,
          t_start: 0,
          identifier: randomString(10),
        }
        if (copyOfCurrentLyricsForTimings.length > 0) {
          timing = copyOfCurrentLyricsForTimings[0]
          copyOfCurrentLyricsForTimings.splice(0, 1)
        }
        newLyrics.push({
          duration: timing.duration,
          end_adj: timing.end_adj,
          line: newLyricsRaw[i],
          milliseconds: timing.milliseconds,
          start_adj: timing.start_adj,
          t_end: timing.t_end,
          t_start: timing.t_start,
          identifier: randomString(10),
        })
      }
    }
    setLyrics(newLyrics)
    return newLyrics
  }

  useEffect(() => {
    const videoEl = videoElement.current

    const handleLoadedMetadata = () => {
      if (videoEl?.duration) {
        const durationMs = videoEl.duration * 1000
        setVideoDuration(durationMs)
      }
    }

    if (videoEl) {
      videoEl.load()
      videoEl.addEventListener('loadedmetadata', handleLoadedMetadata)
    }

    return () => {
      if (videoEl) {
        videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata)
      }
    }
  }, [videoElement.current])

  return (
    <Wrapper>
      {isFeedbackPrompt && (
        <PromptWithText
          title={'Describe the issue with this song'}
          subtitle={''}
          primaryAction={'CANCEL'}
          onPrimaryClick={() => {
            setIsFeedbackPrompt(false)
          }}
          secondaryAction={'SEND'}
          onSecondaryClick={comment => {
            handleOnReport(comment)
          }}
          isSecondaryActionDestructive={false}
          onCloseClick={() => {
            setIsFeedbackPrompt(false)
          }}
        />
      )}
      {isTransferEndTimingsConfirm && (
        <div
          className="prompt-bg-container"
          onClick={e => {
            if (e.target === e.currentTarget) {
              setIsTransferEndTimingsConfirm(false)
            }
          }}
        >
          <div className="prompt-container">
            <div className="prompt-title-subtitle-container">
              <p className="prompt-title-text">Transfer end timings?</p>
              <p className="prompt-subtitle-text">
                When timing lyrics, use the <kbd>U</kbd> key to mark the start of each line as it plays. 
                <br /><br />
                Once you&apos;ve set a start time for every line, use <kbd>this button</kbd> to automatically fill in the end times. <i>This will overwrite any end times you&apos;ve already set.</i>
                <br /><br />
                After that, give the song one more listen from the start and adjust any lines that feel out of sync until every lyric lands right on time.
              </p>
            </div>
            <div className="prompt-actions">
              <button
                className="prompt-action-button"
                onClick={() => setIsTransferEndTimingsConfirm(false)}
              >
                CANCEL
              </button>
              <button
                className="prompt-action-button prompt-destructive-action-button"
                onClick={handleTransferEndTimings}
              >
                CONFIRM
              </button>
            </div>
          </div>
        </div>
      )}
      {!isLoading && (
        <Header>
          {isEmailInSongManagementTeam(getCurrentUser()?.email) ? (
            <Breadcrumbs
              items={[
                {
                  text: 'All Songs',
                  onClick: () => {
                    handleOnNavigationToAllSongs()
                  },
                },
                {
                  text: 'Edit Text',
                  onClick: () => {},
                },
              ]}
            />
          ) : (
            <Breadcrumbs
              items={
                !songData?.artist?.name
                  ? [
                      {
                        text: 'All Songs',
                        onClick: () => {
                          handleOnNavigationToAllSongs()
                        },
                      },
                    ]
                  : [
                      {
                        text: 'All Songs',
                        onClick: () => {
                          handleOnNavigationToAllSongs()
                        },
                      },
                      {
                        text: formatStringInNav(songData?.artist?.name),
                        onClick: () => {
                          handleOnNavigationToSpecificArtistClick()
                        },
                      },
                      {
                        text: 'Songs',
                        onClick: () => {
                          handleOnNavigationToArtistSongsClick()
                        },
                      },
                      {
                        text: formatStringInNav(songData?.name),
                        onClick: () => {
                          handleOnNavigationToSongClick()
                        },
                      },
                      {
                        text: 'Edit Timing',
                        onClick: () => {},
                      },
                    ]
              }
            />
          )}
          {songData && (
            <div className="edit-song-artist-container-holder">
              <ArtistAndSongName
                song={songData}
                badge={
                  <PublicationBadge
                    size="header"
                    status={songData.is_public_visible ? 'published' : 'unpublished'}
                    text={songData.is_public_visible ? 'Published' : 'Unpublished'}
                    onClick={handleOnHide}
                    disabled={!songData.has_lyrics && !songData.is_public_visible}
                    tooltip={
                      !songData.has_lyrics && !songData.is_public_visible
                        ? "This song doesn't have any lyrics. Click 'Edit Lyrics' to add and sync them with the song before publishing."
                        : !songData.is_public_visible
                        ? "Click to make this song visible in the Feels app. It's suggested to sync verify the song timing before publishing."
                        : "Click to hide this song from the Feels app. Users will no longer see it or Feels from it."
                    }
                  />
                }
              />
              {isSavingSong ? (
                <ScaleLoader color="#FF00FF" loading={isSavingSong} />
              ) : (
                <Actions>
                  <div style={{ position: 'relative' }}>
                    <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                    <button
                      className={
                        !showLyricsEditText
                          ? 'edit-song-save-button'
                          : 'edit-song-report-btn'
                      }
                      onClick={() => {
                        if (feelsSyncStatus !== 'pending' && !isSyncVerified) {
                          handleChangeOfEditMode(showLyricsEditText ? true : false)
                        }
                      }}
                      disabled={feelsSyncStatus === 'pending' || isSyncVerified}
                    >
                      {!showLyricsEditText ? 'EDIT LYRICS' : 'CANCEL'}
                    </button>
                  </div>
                  {!showLyricsEditText && (
                    <button
                      className="edit-song-report-btn"
                      onClick={() => {
                        handleOnShowReport()
                      }}
                    >
                      REPORT
                    </button>
                  )}
                  {!showLyricsEditText && (
                    <div style={{ position: 'relative' }}>
                      <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                      <div className="sync-verified-btn">
                        <img
                          width="24"
                          height="24"
                          style={{ cursor: feelsSyncStatus === 'pending' ? 'not-allowed' : 'pointer' }}
                          src={isSyncVerified ? checkmark_full : checkmark_empty}
                          onClick={() => {
                            if (feelsSyncStatus !== 'pending') {
                              setHasEdited(true)
                              setIsSyncVerified(!isSyncVerified)
                            }
                          }}
                        />
                        <span className="span-sync-verified-btn">Sync Verified</span>
                      </div>
                    </div>
                  )}
                  <div style={{ position: 'relative' }}>
                    <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                    <button
                      className={
                        hasEdited
                          ? 'edit-song-save-button'
                          : 'edit-song-save-button-disabled'
                      }
                      onClick={() => {
                        if (hasEdited && feelsSyncStatus !== 'pending') {
                          saveCurrentEdits()
                        }
                      }}
                      disabled={feelsSyncStatus === 'pending'}
                    >
                      {showLyricsEditText ? 'SAVE LYRICS' : 'SAVE'}
                    </button>
                  </div>
                </Actions>
              )}
            </div>
          )}
        </Header>
      )}


      {!isLoading && songData && songData?.video?.video?.url ? (
        hasDeviceAccess ? (
          <div className={`edit-song-lyrics-video-container ${classes.main}`}>
            <div className="edit-lyrics-column">
              {!showLyricsEditText && (
                <div
                  className="top-of-lyrics-column"
                  ref={topOfLyricsColumnElement}
                >
                  <div className="video-offset-input-holder" style={{ position: 'relative' }}>
                    <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                    <input
                      className="video-offset-input-field"
                      placeholder={'Enter Seconds to adjust video...'}
                      onChange={e => {
                        if (feelsSyncStatus !== 'pending' && !isSyncVerified) {
                          handleOnVideoOffsetChange(e)
                        }
                      }}
                      value={videoOffset}
                      disabled={feelsSyncStatus === 'pending' || isSyncVerified}
                    />
                  </div>
                  <div style={{ position: 'relative' }}>
                    <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                    <p className="video-offset-label">Video Offset (in ms)</p>
                  </div>
                  <div className="edit-song-action-buttons-container">
                    <div className="edit-song-left-buttons">
                      <div style={{ position: 'relative' }}>
                        <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                        <button
                          className="edit-song-btn"
                          onClick={() => {
                            if (feelsSyncStatus !== 'pending' && !isSyncVerified) {
                              handleSetCurrentVideoPlayerToLineAction()
                            }
                          }}
                          disabled={feelsSyncStatus === 'pending' || isSyncVerified}
                        >
                          {currentlySelectedLine !== null
                            ? 'ADJUST ALL LINES AHEAD WITH PLAYER TIME'
                            : 'SET PLAYER TIME AS VIDEO OFFSET'}
                        </button>
                      </div>

                      <div style={{ position: 'relative' }}>
                        <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                        <div className="sync-verified-btn">
                          <img
                            width="24"
                            height="24"
                            style={{ cursor: feelsSyncStatus === 'pending' ? 'not-allowed' : 'pointer' }}
                            src={isLoopingLines ? checkmark_full : checkmark_empty}
                            onClick={() => {
                              if (feelsSyncStatus !== 'pending') {
                                setIsLoopingLines(!isLoopingLines)
                              }
                            }}
                          />
                          <span className="span-sync-verified-btn">Loop Lines</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="edit-song-right-buttons">
                      {songData?.timing_source && (
                        <div
                          className={classes.timingSourceBadge}
                          data-source={songData.timing_source}
                          title={songData.timed_at ? `Timed ${songData.timed_at}` : 'How this song\'s lyric timing was produced'}
                        >
                          {songData.timing_source?.startsWith('youtube:')
                            // Ingest's YouTube lyrics carry their underlying
                            // provider in the tracking string (e.g.
                            // "youtube:Musixmatch", "youtube:LyricFind") —
                            // the lyrics plan §2 / processor.js song_lyrics().
                            ? `YouTube Music (${songData.timing_source.slice('youtube:'.length)}) timing`
                            : ({
                              'lyricfind': 'LyricFind timing',
                              'manual': 'Manual timing',
                              'fast-time': 'FeelsSync fast-time',
                              'quick': 'FeelsSync QuickTime',
                              'kaldi-legacy': 'FeelsSync (legacy)',
                              'lrclib': 'LRCLIB timing',
                              'youtube': 'YouTube Music timing',
                            }[songData.timing_source] || songData.timing_source)}
                        </div>
                      )}
                      {isInternal && (
                        <FeelsSyncButton
                          song={songData}
                          hasUnsavedEdits={hasEdited}
                          onBeforeSync={beforeFeelsSync}
                          onSyncStatusChange={setFeelsSyncStatus}
                          onSyncComplete={handleFeelsSyncComplete}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
              {songData?.check_lyrics && (
                <div className={classes.checkLyricsBanner}>
                  ⚠ Something doesn&apos;t look right about these lyrics — please
                  verify all words match the song (intros/skits, clean vs
                  explicit versions, etc.)
                </div>
              )}
              <div className={classes.lyricsColumn}>
                <EditLyrics
                  onLyricSelected={line => {
                    handleLyricSelected(line)
                  }}
                  videOffset={currentVideoOffset}
                  lyrics={lyrics}
                  currentlySelectedLine={currentlySelectedLine}
                  currentlyHighlightedLine={currentlyHighlightedLine}
                  isShiftDown={isShiftDown}
                  isAltDown={isAltDown}
                  isXDown={isXDown}
                  isEnabled={!showLyricsEditText}
                  canEditLineStructure={!showLyricsEditText && !editingLocked}
                  canEditTiming={!showLyricsEditText && !editingLocked}
                  handleIncrementDecrementButton={(
                    line,
                    isStart,
                    isDecerement,
                    isShiftDown,
                    isAltDown,
                    isXDown
                  ) => {
                    handleIncrementDecrementButton(
                      line,
                      isStart,
                      isDecerement,
                      isShiftDown ? 1000 : isAltDown ? 20 : isXDown ? 300 : 100,
                      true
                    )
                  }}
                  handleStartEndInputValueChange={(
                    line,
                    value,
                    isStartTime
                  ) => {
                    handleStartEndInputValueChange(line, value, isStartTime)
                  }}
                  showOverlay={feelsSyncStatus === 'pending'}
                  overlayMessage="FeelsSync in progress..."
                  onInsertLine={(anchorId, position) => insertLineAt(anchorId, position)}
                  onDeleteLine={(id) => deleteLineAt(id)}
                  onUndoDeleteLine={(id) => undoDeleteLineAt(id)}
                  editingLineId={editingLineId}
                  onStartEditing={(id) => startEditing(id)}
                  onStopEditing={(id) => stopEditingAndPurgeIfEmpty(id)}
                  onSetLineText={(id, text) => setLineText(id, text)}
                  onAdvanceEditing={() => advanceEditingToNext()}
                  onSplitLine={(id, cursorIndex) => splitLineAtCursor(id, cursorIndex)}
                />
                {isInternal && (
                <div style={{ padding: '16px 0 8px', display: 'flex', justifyContent: 'center' }}>
                  <button
                    className="edit-song-btn"
                    onClick={() => setIsTransferEndTimingsConfirm(true)}
                  >
                    TRANSFER END TIMINGS
                  </button>
                </div>
                )}
              </div>
            </div>
            <div className="edit-song-video-column-wrapper">
              <div className="edit-song-video-column" ref={videoColumnElement}>
                <div
                  className={
                    showLyricsEditText
                      ? 'edit-song-text'
                      : 'edit-song-text do-not-show'
                  }
                >
                  <textarea
                    className="lyrics-freeform-textarea"
                    name="freeform"
                    cols="55"
                    defaultValue={getLyricsInOneTextBlock()}
                    onInput={() => {
                      lyricsTextIsChanging()
                    }}
                  ></textarea>
                </div>
                <div
                  className={
                    showLyricsEditText
                      ? 'edit-song-video-and-buttons-container do-not-show'
                      : 'edit-song-video-and-buttons-container'
                  }
                >
                  <div className="edit-song-video-player-container">
                    <video
                      ref={videoElement}
                      className="edit-song-video-player"
                      controls
                      autoPlay={true}
                      muted={true}
                      loop={false}
                      src={songData?.video?.video?.url}
                      controlsList="nofullscreen"
                    />
                  </div>
                  <div className="waveform-container">
                    <div className="wave" ref={waveformElement} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <DevicesRestrictionsNotification />
        )
      ) : (
        <>
          {!isLoading && (
            <h1 style={{ color: 'white' }}>
              {errorMessage ? errorMessage : ''}
            </h1>
          )}
          {isLoading && <Loader isLoading={isLoading} />}
        </>
      )}
    </Wrapper>
  )
}
